import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, Button, Icon, Pagination } from "semantic-ui-react";

const ITEMS_PER_PAGE = 15;
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListUsers.scss";
import _ from "lodash";
// import Loader from "./Loader";
// import UserItem from "./UserItem";
// import { useAuth } from "../hooks/useAuth";
// import { useLanguage } from "../hooks/useLanguage";
// import { isAdmin, isMaster, hasPermission } from "../utils/roles";
// import userController from "../controllers/userController";
// import permissionController from "../controllers/permissionController";
import { ErrorAccessDenied } from "../../../../pages/admin/Error";
import { LoadingSpinner } from "../../../Shared";
import { useLanguage } from "../../../../contexts";
import { useAuth } from "../../../../hooks";
import { UserItem } from "../UserItem";
import { Permission, User } from "../../../../api";
import { isAdmin,isMaster, hasPermission } from "../../../../utils/checkPermission";

const userController=new User();
const permissionController=new Permission();

export function ListUsers({ usersActive, usersStatus, reload, onReload, onOpenModal, onOpenInviteModal }) {
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [usersFilter, setUsersFilter] = useState({});
  const [originalUsersByCompany, setOriginalUsersByCompany] = useState({});
  const [allUsersFilter, setAllUsersFilter] = useState([]);
  const [pagesMap, setPagesMap] = useState({});
  const [permissionsByRole, setPermissionsByRole] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState(null);

  const { accessToken, user: { role, site, company } } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // --- Cargar permisos ---
  useEffect(() => {
    (async () => {
      try {
        if (role) {
          const response = await permissionController.getPermissionsByRole(
            accessToken,
            role._id,
            true
          );
          setPermissionsByRole(response);
        }
      } catch (err) {
        console.error(err);
        setPermissionsByRole([]);
      }
    })();
  }, [role, accessToken]);

  // --- Cargar usuarios ---
  useEffect(() => {
    (async () => {
      try {
        setUsers(null);
        setLoadError(false);
        let response = [];

        if (usersStatus) {
          // Consulta por status (invited / pending)
          if (isAdmin(role) && company) {
            response = await userController.getUsersByCompanyAndStatus(accessToken, company, usersStatus);
          } else if (isMaster(role)) {
            response = await userController.getUsersByStatus(accessToken, usersStatus);
          }
        } else {
          // Consulta clásica por active
          if (isAdmin(role) && company && !site) {
            response = await userController.getUsersByCompany(accessToken, company, usersActive);
          } else if (isAdmin(role) && site && company) {
            response = await userController.getUsersBySite(accessToken, site, usersActive);
          } else if (isMaster(role)) {
            response = await userController.getUsers(accessToken, usersActive);
          }
        }

        let result = response.map(u => ({ ...u, key: u._id }));

        // --- Filtrar por la empresa del usuario si tiene company (solo para consultas por active) ---
        if (!usersStatus && company) {
          result = result.filter(u => u.company?._id === company._id);
        }

        setUsers(result);
        setAllUsersFilter(result);

        // --- Agrupar por empresa ---
        const grouped = result.reduce((acc, user) => {
          const name = user.company?.name || t("no_company");
          if (!acc[name]) acc[name] = [];
          acc[name].push(user);
          return acc;
        }, {});

        setUsersFilter(grouped);
        setOriginalUsersByCompany(grouped);
      } catch (err) {
        console.error(err);
        setUsers([]);
        setAllUsersFilter([]);
        setUsersFilter({});
        setOriginalUsersByCompany({});
        setLoadError(true);
      }
    })();
  }, [usersActive, usersStatus, reload]);

  // --- Mantener válida la empresa seleccionada en el dropdown (null = todas) ---
  useEffect(() => {
    const keys = Object.keys(usersFilter);
    setSelectedCompanyKey((prev) => (prev && keys.includes(prev) ? prev : null));
  }, [usersFilter]);

  if (!users) return <LoadingSpinner />;
  if (loadError) return (
    <div className="list-error-state">
      <Icon name="exclamation triangle" className="list-error-state__icon" />
      <p className="list-error-state__text">{t("error_loading_users") || "Error al cargar los usuarios."}</p>
      <button type="button" className="list-error-state__retry" onClick={() => window.location.reload()}>
        Reintentar
      </button>
    </div>
  );
  if (users.length === 0) return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        {onOpenInviteModal && (
          <Button icon className="add-user-btn add-user-btn--invite" title={t("invite")} onClick={onOpenInviteModal}>
            <Icon name="mail" />
          </Button>
        )}
        {onOpenModal && (
          <Button icon className="add-user-btn" title={t("create")} onClick={onOpenModal}>
            <Icon name="plus" />
          </Button>
        )}
      </div>
      <div className="list-empty-state">
        <Icon name="folder open outline" className="list-empty-state__icon" />
        <p className="list-empty-state__text">{t("there_is_no_user")}</p>
      </div>
    </div>
  );

  // --- Helpers de paginación ---
  const getPage = (key) => pagesMap[key] || 1;
  const setPage = (key, page) => setPagesMap(prev => ({ ...prev, [key]: page }));
  const paginate = (list, key) => {
    const p = getPage(key);
    return list.slice((p - 1) * ITEMS_PER_PAGE, p * ITEMS_PER_PAGE);
  };

  const companyEntries = Object.entries(usersFilter);
  const showCompanyDropdown = !company && companyEntries.length > 0;
  const isAllView = !company && !selectedCompanyKey;
  const activeKey = company ? "no_company" : (selectedCompanyKey || "__all__");
  const activeGroup = isAllView ? allUsersFilter : (usersFilter[activeKey] || []);
  const originalGroup = isAllView ? users : (originalUsersByCompany[activeKey] || []);
  const pagedGroup = paginate(activeGroup, activeKey);
  const totalPages = Math.ceil(activeGroup.length / ITEMS_PER_PAGE);
  const dropdownCompanies = companyEntries.map(([name]) => ({ _id: name, name }));
  const showCompanyColumn = isMaster(role) && isAllView;
  const showSiteColumn = isMaster(role) || (isAdmin(role) && !site);

  return (
    <div className="users-list">
      {isMaster(role) || isAdmin(role) || hasPermission(permissionsByRole, role._id, "users", "view") ? (
        <div>
          <div className="users-list__card">
            <div className="users-list__filters">
              <SearchStandardUser
                dataOrigin={originalGroup}
                setData={(filteredUsers) => {
                  if (isAllView) {
                    setAllUsersFilter(filteredUsers);
                  } else {
                    setUsersFilter(prev => ({ ...prev, [activeKey]: filteredUsers }));
                  }
                  setPage(activeKey, 1);
                }}
                t={t}
              />
              {showCompanyDropdown && (
                <CompanyDropdown
                  companies={dropdownCompanies}
                  value={activeKey}
                  onChange={setSelectedCompanyKey}
                  placeholder={t("companies")}
                />
              )}
              <div className="users-list__filter-actions">
                {onOpenInviteModal && (
                  <Button icon className="add-user-btn add-user-btn--invite" title={t("invite")} onClick={onOpenInviteModal}>
                    <Icon name="mail" />
                  </Button>
                )}
                {onOpenModal && (
                  <Button icon className="add-user-btn" title={t("create")} onClick={onOpenModal}>
                    <Icon name="plus" />
                  </Button>
                )}
              </div>
            </div>
            <div className="users-table-scroll">
              <Table className="users-table unstackable rtable-cards row-hover">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("lastname")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("email")}</Table.HeaderCell>
                    {showCompanyColumn && <Table.HeaderCell>{t("company")}</Table.HeaderCell>}
                    {showSiteColumn && <Table.HeaderCell>{t("site")}</Table.HeaderCell>}
                    <Table.HeaderCell>{t("role")}</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pagedGroup.map(user => (
                    <UserItem
                      key={user._id}
                      user={user}
                      onReload={onReload}
                      hideActivate={usersStatus === 'invited'}
                      showCompanyColumn={showCompanyColumn}
                      showSiteColumn={showSiteColumn}
                    />
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
          {totalPages > 1 && (
            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "center" }}>
              <Pagination
                activePage={getPage(activeKey)}
                totalPages={totalPages}
                onPageChange={(_, { activePage }) => setPage(activeKey, activePage)}
                size="tiny"
              />
            </div>
          )}
        </div>
      ) : (
        <ErrorAccessDenied />
      )}
    </div>
  );
}

function CompanyDropdown(props) {
  const { companies, value, onChange, placeholder } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="users-list__company-dd" ref={ref}>
      <button type="button" className={`users-list__company-dd-toggle${open ? " users-list__company-dd-toggle--open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <span>{placeholder}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`users-list__company-dd-arrow${open ? " users-list__company-dd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className="users-list__company-dd-menu">
          {companies.map((c) => (
            <button
              type="button"
              key={c._id}
              className={`users-list__company-dd-item${c._id === value ? " users-list__company-dd-item--active" : ""}`}
              onClick={() => { onChange && onChange(c._id); setOpen(false); }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// -------------------- Buscador --------------------
function SearchStandardUser({ dataOrigin = [], setData, t }) {
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const debouncedFilter = useMemo(
    () => _.debounce(value => {
      const filtered = dataOrigin.filter(user =>
        ["firstname", "lastname", "email"].some(key =>
          user[key]?.toLowerCase().includes(value.toLowerCase())
        )
      );
      setData(filtered);
      setIsLoading(false);
    }, 300),
    [dataOrigin, setData]
  );

  const handleSearchChange = useCallback(
    (e, { value }) => {
      setSearchValue(value);

      if (!value) {
        setData(dataOrigin); // restaurar lista completa
        setIsLoading(false);
        debouncedFilter.cancel();
        return;
      }

      setIsLoading(true);
      debouncedFilter(value);
    },
    [dataOrigin, setData, debouncedFilter]
  );

  return (
    <div className="search-user-wrapper">
      <img src={searchSvg} alt="search" className="search-user-icon" />
      <input
        className="search-user-input"
        placeholder={t("search")}
        value={searchValue}
        onChange={(e) => handleSearchChange(e, { value: e.target.value })}
      />
    </div>
  );
}